import {AppLoadContext} from '@shopify/remix-oxygen';
import {Storefront} from '@shopify/hydrogen';
import {MediaEdge} from '@shopify/hydrogen/storefront-api-types';
import * as _ from 'lodash';
import {COLLECTION_CONTENT_QUERY, getMetaObjectQuery} from '~/data/fragments';
import {SECTION_TYPES} from '~/lib/const';
import storefrontFetcher from '~/lib/storefrontFetcher';
import {IRoute, MetaFields, MetafieldStringData} from '~/types';
import {MetaobjectsQuery} from '~/data/MetaobjectsQuery';
export const defaultImage = (title: string, width = 214, height = 314) => {
  return `https://placehold.co/${width}x${height}/3F6FA1/white?text=${_.get(
    title?.split(' '),
    '[0]',
  )}`;
};
export const defaultSizableImage = ({
  title,
  width,
  height,
}: {
  title: string;
  width: number;
  height: number;
}) => {
  return `https://placehold.co/${width}x${height}/3F6FA1/white?text=${_.get(
    title?.split(' '),
    '[0]',
  )}`;
};

export const generateDefaultImages = () => {
  return [
    {
      __typename: 'MediaImage',
      mediaContentType: 'IMAGE',
      alt: '',
      previewImage: {
        url: defaultImage('no-image'),
      },
      id: 'gid://shopify/MediaImage/33193486188844',
      image: {
        url: defaultImage('no-image'),
        width: 600,
        height: 400,
      },
    },
  ] as MediaEdge['node'][];
};

export function paragraphToHTML(content: any) {
  let html = '';
  try {
    const parsed = JSON.parse(content);

    parsed.children.forEach((node: any) => {
      switch (node.type) {
        case 'paragraph':
          html += `<p>`;
          node.children.forEach((item: any) => {
            if (item.type === 'text' && item.bold) {
              html += `<strong>${item.value}</strong>` + ' ';
            } else if (item.type === 'text' && item.italic) {
              html += `<em>${item.value}</em>` + ' ';
            } else if (item.type === 'text') {
              html += `${item.value}` + ' ';
            }
            if (item.type === 'link' && item.bold) {
              html +=
                `<a href="${item.url}" target="${item.target}"><strong>${item.children[0].value}</strong></a>` +
                ' ';
            } else if (item.type === 'link' && item.italic) {
              html +=
                `<a href="${item.url}" target="${item.target}"><em>${item.children[0].value}</em></a>` +
                ' ';
            } else if (item.type === 'link') {
              html +=
                `<a href="${item.url}" target="${item.target}">${item.children[0].value}</a>` +
                ' ';
            }
          });
          html += `</p>`;
          break;
      }
    });
  } catch (error) {}

  return html;
}
export function toHTML(content: any) {
  let html = '';
  try {
    const parsed = JSON.parse(content);

    parsed.children.forEach((node: any) => {
      switch (node.type) {
        case 'heading':
          html += `<h${node.level}>${node.children[0].value}</h${node.level}>`;
          break;
        case 'list':
          html += `<${
            node.listType === 'unordered' ? 'ul' : 'ol'
          } class="list">`;
          node.children.forEach((item: any) => {
            let temp = '';
            if (_.get(item, 'children')) {
              temp += '<li>';
              item.children.map((it) => {
                if (it.type === 'link') {
                  temp +=
                    `<a href="${it.url}" target="${it.target}">${it.children[0].value}</a>` +
                    ' ';
                } else if (it.type === 'link' && it.italic) {
                  temp +=
                    `<a href="${it.url}" target="${it.target}"><em>${it.children[0].value}</em></a>` +
                    ' ';
                } else if (item.type === 'link') {
                  temp +=
                    `<a href="${it.url}" target="${it.target}">${it.children[0].value}</a>` +
                    ' ';
                }
              });
              temp += '</li>';
            }
            if (temp.replaceAll(' ', '') != '<li></li>') {
              html += temp;
            } else {
              html += `<li>${item.children[0].value} ${_.get(
                item.children,
                '[1].value',
                '',
              )}</li>`;
            }
          });
          html += `<${node.listType === 'unordered' ? '/ul' : '/ol'}>`;
          break;
        case 'paragraph':
          html += `<p>`;
          node.children.forEach((item: any) => {
            if (item.type === 'text' && item.bold) {
              html += `<strong>${item.value}</strong>` + ' ';
            } else if (item.type === 'text' && item.italic) {
              html += `<em>${item.value}</em>` + ' ';
            } else if (item.type === 'text') {
              html += `${item.value}` + ' ';
            }
            if (item.type === 'link' && item.bold) {
              html +=
                `<a href="${item.url}" target="${item.target}"><strong>${item.children[0].value}</strong></a>` +
                ' ';
            } else if (item.type === 'link' && item.italic) {
              html +=
                `<a href="${item.url}" target="${item.target}"><em>${item.children[0].value}</em></a>` +
                ' ';
            } else if (item.type === 'link') {
              html +=
                `<a href="${item.url}" target="${item.target}">${item.children[0].value}</a>` +
                ' ';
            }
          });
          html += `</p>`;
          break;
      }
    });
  } catch (error) {}

  return html;
}

export const getMetaObject = ({
  metaobject,
  key = 'type',
  value = 'reference',
}: {
  metaobject: {fields: MetafieldStringData[]} | undefined;
  key: string;
  value?: any;
}) => {
  const meta = _.find(metaobject?.fields, (item) => {
    if (_.get(item, 'key') === key) {
      return true;
    }
  });
  return _.get(meta, value) || _.get(meta, 'references');
};

export const getMetaObjectValue = ({
  metaobject,
  key = 'type',
  defaultValue = '',
}: {
  metaobject: {fields: MetafieldStringData[]} | undefined;
  key: string;
  defaultValue?: any;
}) => {
  const meta = _.find(metaobject?.fields, (item) => {
    if (_.get(item, 'key') === key) {
      return true;
    }
  });
  return _.get(meta, 'value', defaultValue);
};

export const getMetaObjectSections = (metafields: MetaFields[]) => {
  const sections: any[] = [];
  metafields.map((item) => {
    if (item?.reference) {
      sections.push(item.reference);
    }
    if (item?.references) {
      _.map(_.get(item, 'references.nodes', []), (node) => {
        sections.push(node);
      });
    }
  });

  return sections;
};

export const HEADER_STYLE = {
  DARK_HEADER: 'dark_header',
  LIGHT_HEADER: 'light_header',
};

// deprecated
export const getPageSections = (metafields: any) => {
  return [
    ...getMetaObjectSections(_.get(metafields, SECTION_TYPES.HERO_SECTION)),
    ...getMetaObjectSections(_.get(metafields, SECTION_TYPES.PAGE_BANNER)),
  ];
};

export const findNode = (data: any, key: string) => {
  return _.find(data, (item) => {
    if (_.get(item, 'key') === key) {
      return true;
    }

    if (_.get(item, 'type') === key) {
      return true;
    }
  });
};
export const findNodeValue = (data: any, key: string, value = 'value') => {
  return _.get(findNode(data, key), value);
};
export const findFieldsNode = (data: any, key: string) => {
  return findNode(_.get(data, 'fields'), key);
};

export const findValueFromFields = (data: any, key: string) => {
  return _.get(findFieldsNode(data, key), 'value');
};

export const getReference = (data: any) => {
  return _.get(data, 'reference');
};

export const getDataFromKey = (data: any, key: string) => {
  return _.get(data, key);
};

export const getFromReference = ({
  data,
  parent,
  key,
}: {
  data: any;
  parent: string;
  key: string;
}) => {
  return findFieldsNode(getReference(findFieldsNode(data, parent)), key);
};

export const getValueFromReference = ({
  data,
  parent,
  key,
}: {
  data: any;
  parent: string;
  key: string;
}) => {
  return _.get(getFromReference({data, parent, key}), 'value');
};

export const getSectionBlocks = (
  pageData: any,
  section_key = 'sections[0].references.nodes',
) => {
  const sections = _.get(pageData, `${section_key}`);
  return _.map(sections, (section: any, index: number) => {
    if (index !== 10) {
      const blocks = _.get(section, 'fields');
      const block = findNode(
        getMetaObjectSections([findNode(blocks, 'blocks')]),
        'block',
      );
      const style = _.get(
        findNode(_.get(block, 'fields'), 'style'),
        'reference.fields[0].value',
        '',
      ).toLowerCase();

      return {...block, type: style};
    }
  });
};

export const sectionHasKey = (sections: any[], key: string) => {
  return _.map(sections, (item) => {
    const fields = _.get(item, 'fields');
    return findNodeValue(fields, 'style');
  })?.includes(key);
};

export const blockHasKey = (blocks: any[], key: string) => {
  return _.map(blocks, (it) => get(it, 'style'))?.includes(key);
};

export const processPageBlocks = async ({
  context,
  page,
  articles,
  product_collections,
  faqs,
}: {
  context: AppLoadContext;
  page: any[];
  articles: {id: string}[];
  product_collections: {id: string}[];
  faqs: {id: string}[];
}) => {
  processFeatures(page);
  const blocks = getBlocks(page);
  let allArticles = [];
  let collectionProducts = [];
  let allFaqs = {};
  const blockCollections = [];

  _.map(blocks, (block) => {
    _.map(_.get(block, 'collections', []), (collection) => {
      blockCollections.push({
        id: _.get(collection, 'id') || _.get(collection, 'value'),
      });
    });
  });
  if (blockHasKey(blocks, 'articles')) {
    allArticles = await getMetaObjectByIds({
      context,
      ids: _.map(articles, (it) => _.get(it, 'id')),
    });
  }

  if (blockHasKey(blocks, 'q&a')) {
    const _faqs = await getMetaObjectByIds({
      context,
      ids: _.map(faqs, (it) => _.get(it, 'id')),
    });
    _.map(_faqs, (item, idx) => {
      const category = _.get(findFieldsNode(item, 'category'), 'value');
      if (Object.keys(allFaqs).includes(category)) {
        allFaqs[category].push(item);
      } else {
        allFaqs[category] = [item];
      }
    });
  }

  if (blockHasKey(blocks, 'products')) {
    collectionProducts = await Promise.all(
      _.map(
        _.map(blockCollections, (it) => _.get(it, 'id')),
        (it) =>
          storefrontFetcher({
            context,
            query: COLLECTION_CONTENT_QUERY,
            variables: {
              id: it,
            },
          }),
      ),
    );
  }

  return {blocks, allArticles, collectionProducts, allFaqs};
};

export const getPageSectionSBlocks = (
  pageData: any,
  section_key: string,
  key?: string,
) => {
  const sections = _.get(pageData, `${section_key}`);
  const li: any[] = [];

  _.map(sections, (section: any, index: number) => {
    const blocks = _.get(
      findNode(_.get(section, 'fields'), 'blocks'),
      `references.nodes`,
    );

    _.map(blocks, (item: any, idx) => {
      const style = _.get(
        findFieldsNode(item, 'style'),
        'value',
      )?.toLowerCase();
      li.push({...item, type: style});
    });
  });
  return li;
};

export const getPageSectionBlocks = (
  pageData: any,
  section_key = 'sections[0].references.nodes',
  key?: string,
) => {
  const sections = _.get(pageData, `${section_key}`);

  return _.map(sections, (section: any, index: number) => {
    const blocks = _.get(
      findNode(_.get(section, 'fields'), 'blocks'),
      'references.nodes[0]',
    );

    const style = _.get(
      findFieldsNode(blocks, 'style'),
      'value',
    )?.toLowerCase();
    return {...blocks, type: style};
  });
};

export function slugify(string: string) {
  return string
    ?.toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]+/g, '')
    .replace(/\-\-+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
}

export const processReferences = (data: any, key: string) => {
  const nodes = getDataFromKey(findFieldsNode(data, key), 'references.nodes');
  return _.map(nodes, (it) => {
    return {
      name: _.get(it, 'handle'),
      id: _.get(it, 'id'),
    };
  });
};

const getReferenceFields = (data: any) => {
  return _.get(data, 'reference.fields');
};
export const getReferenceNodes = (data: any) => {
  return _.get(data, 'references.nodes');
};

export const processDefaultMenuItems = (data: any) => {
  const routes: IRoute[] = [];
  const menus = _.get(
    findNode(_.get(data, 'metaobject.fields'), 'menu'),
    'references.nodes',
  );
  _.map(menus, (item: any) => {
    const id = _.get(findFieldsNode(item, 'route'), 'value');
    let handle = _.get(findFieldsNode(item, 'route'), 'reference.handle');
    const arr = Array.from(handle)[0];
    if (arr !== '/') {
      handle = `/${handle}`;
    }
    routes.push({
      name: findValueFromFields(item, 'label'),
      route: findValueFromFields(item, 'route'),
      id,
      handle: `/pages${handle}`,
      slug: slugify(findValueFromFields(item, 'label')),
    });
  });

  return routes;
};
// deprecated
export const processMenuItems = (data: any) => {
  const items = getDataFromKey(
    findFieldsNode(getDataFromKey(data, 'metaobject'), 'menu'),
    'references.nodes',
  );
  const routes: IRoute[] = [];
  _.map(items, (item) => {
    const id = _.get(findFieldsNode(item, 'route'), 'value');
    let handle = _.get(findFieldsNode(item, 'route'), 'reference.handle');
    const arr = Array.from(handle)[0];
    if (arr !== '/') {
      handle = `/${handle}`;
    }
    routes.push({
      name: findValueFromFields(item, 'label'),
      route: findValueFromFields(item, 'route'),
      id,
      handle: `/pages${handle}`,
      slug: slugify(findValueFromFields(item, 'label')),
    });
  });
  return routes;
};

export const getKeyValue = (data: any[]) => {
  return _.map(data, (it) => {
    return {
      name: findValueFromFields(it, 'name'),
      value: findValueFromFields(it, 'url'),
      id: findValueFromFields(it, 'id'),
    };
  });
};

const METAOBJECT_ID_PREFIX = 'gid://shopify/Metaobject/';

const getShopifyID = (key: string, id: string) => {
  return id?.replace(key, '');
};

export const getMetaObjectID = (id: string) => {
  return getShopifyID(METAOBJECT_ID_PREFIX, id);
};

export const buildMetaObjectID = (id: string) => {
  return `${METAOBJECT_ID_PREFIX}${id}`;
};

export const getDefaultCache = (storefront: Storefront) => {
  return storefront.CacheNone(); // enable cache on production
};

export const getRandomBoolean = () => {
  return _.sample([0, 1]);
};
export const removeTypeNameFromQuery = (query: string) => {
  return query;
};

const aggregateRefs = (fields: any[]) => {
  return _.map(fields, (item) => {
    let value = _.get(item, 'value');
    if (_.get(item, 'references.nodes')) {
      value = _.map(_.get(item, 'references.nodes'), (i) => i);
      return {
        data: aggregateFields(value),
        key: _.get(item, 'key'),
      };
    }
    return item;
  });
};

export const aggregateFields = (items: any[]) => {
  return _.map(items, (item) => {
    if (_.get(item, 'fields')) {
      let fields = _.map(_.get(item, 'fields'), (i) => i);
      delete item.fields;
      fields = aggregateRefs(fields);
      fields.push(item);
      return fields;
    }
  });
};

export const processArticle = (article: any, language: any, country: any) => {
  let createdAt = new Date().toDateString();
  if (findNodeValue(article, 'created_at')) {
    createdAt = new Intl.DateTimeFormat(`${language}-${country}`, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(new Date(findNodeValue(article, 'created_at')!));
  }
  const content_MD = findNodeValue(article, 'content_md');
  const content = findNodeValue(article, 'content');
  return {
    id: findNodeValue(article, 'article', 'id'),
    title: findNodeValue(article, 'title'),
    media: findNodeValue(article, 'media'),
    isContentMarkdown: isMarkdown(content_MD),
    content_MD,
    content: paragraphToHTML(content),
    author: _.get(
      findNode(
        getDataFromKey(findNodeValue(article, 'authors', 'data'), '[0]'),
        'name',
      ),
      'value',
    ),
    handle: `/blog/${findNodeValue(article, 'article', 'handle')}`,
    createdAt,
  };
};

export {SECTION_TYPES};

export const getProductImage = (product: any) => {
  const images: string[] = [];
  _.map(_.get(product, 'mainMedia'), (item: any) => {
    if (!item) {
      return;
    }
    images.push(_.get(item, 'value'));
  });
  _.map(_.get(product, 'additionalMedia'), (item: any) => {
    if (_.get(item, 'value')) {
      try {
        const values = JSON.parse(_.get(item, 'value'));
        if (!values) {
          return;
        }
        if (!_.isArray(values)) {
          return;
        }
        _.map(values, (it: any) => images.push(it));
      } catch (error) {}
    }
  });
  return images;
};

export const parseProductImages = (images: any) => {
  return _.map(_.uniq(images), (it: any) => {
    return {
      mediaContentType: 'IMAGE',
      alt: '',
      __typename: 'MediaImage',
      previewImage: {
        url: it,
        __typename: 'Image',
      },
      id: 'gid://shopify/MediaImage/34585325699372',
      image: {
        url: it,
        width: 1000,
        height: 1000,
      },
    };
  });
};
export const getProductDetailImages = (product: any) => {
  const images = getProductImage(product);
  if (_.get(images, 'length') === 0) {
    return [];
  }
  return parseProductImages(images);
};

interface IMetaobject {
  id: string;
  type: string;
  handle: string;
  fields: any[];
}
export const getSiteDetails = async (
  context: AppLoadContext,
  site: IMetaobject,
) => {
  const meta = parseJSONWithData(findValueFromFields(site, 'meta'));
  const header = parseJSONWithData(findValueFromFields(site, 'header'));
  const footer = parseJSONWithData(findValueFromFields(site, 'footer'));
  const pages = parseJSONWithData(findValueFromFields(site, 'pages'));
  const menu = parseJSONWithData(findValueFromFields(site, 'menu'));
  const articles = parseJSONWithData(findValueFromFields(site, 'articles'));
  const faqs = parseJSONWithData(findValueFromFields(site, 'faq'));
  const product_collections = parseJSONWithData(
    findValueFromFields(site, 'product_collections'),
  );

  return {
    meta,
    header,
    pages,
    menu,
    footer,
    articles,
    product_collections,
    faqs,
  };
};
export const get = _.get;
export const getBrandingDetails = async (
  context: AppLoadContext,
  branding: any,
) => {
  const home_page = getMetaObject({
    metaobject: branding,
    key: 'home_page',
  });

  const defaultHeader = getMetaObject({
    metaobject: branding,
    key: 'header',
  });
  const pageMenu = findValueFromFields(defaultHeader, 'header_menu');

  const logo = findValueFromFields(defaultHeader, 'header_logo');

  let menuItems = null;
  if (pageMenu) {
    const menu = await context?.storefront?.query(getMetaObjectQuery(), {
      variables: {
        id: pageMenu,
      },
    });

    menuItems = processDefaultMenuItems(menu);
  }
  return {
    logo,
    home_page,
    menuItems,
    pageMenu,
  };
};
export const getMetaObjectByIds = async ({
  context,
  ids,
}: {
  context: AppLoadContext;
  ids: string[];
}) => {
  const metaobjects = await storefrontFetcher({
    context,
    query: MetaobjectsQuery,
    variables: {
      ids,
    },
  });

  return get(metaobjects, 'nodes', []);
};
export const processPageSections = async ({
  sections,
  branding,
  context,
}: {
  sections: any;
  branding: any;
  context: AppLoadContext;
}) => {
  let collectionProducts = [];
  let articles: any[] = [];
  let faqs: any[] = [];
  let allFaqs: any = {};

  const hasProducts = sectionHasKey(sections, SECTION_TYPES.PRODUCTS);
  const hasArticles = sectionHasKey(sections, SECTION_TYPES.ARTICLES);
  const hasFAQs = sectionHasKey(sections, SECTION_TYPES.FAQS);

  if (hasFAQs) {
    faqs = _.get(findFieldsNode(branding, 'q_a'), 'references.nodes');

    _.map(faqs, (item, idx) => {
      const category = _.get(findFieldsNode(item, 'category'), 'value');
      if (Object.keys(allFaqs).includes(category)) {
        allFaqs[category].push(item);
      } else {
        allFaqs[category] = [item];
      }
    });
  }
  if (hasArticles) {
    articles = aggregateFields(
      getReferenceNodes(findFieldsNode(branding, 'articles')),
    );
  }
  if (hasProducts) {
    let collectionsIds = _.get(
      findFieldsNode(findNode(sections, 'products'), 'collections'),
      'value',
    );
    if (collectionsIds) {
      collectionsIds = JSON.parse(collectionsIds);
    }
    const collectionObject = _.get(
      findFieldsNode(branding, 'collections'),
      'value',
    );

    collectionsIds = _.uniqBy(
      _.filter(collectionsIds, (v) => _.includes(collectionObject, v)),
      (e) => e,
    );

    collectionProducts = await Promise.all(
      _.map(collectionsIds, (it) =>
        storefrontFetcher({
          context,
          query: COLLECTION_CONTENT_QUERY,
          variables: {
            id: it,
          },
        }),
      ),
    );
  }

  return {
    articles,
    allFaqs,
    collectionProducts,
  };
};

export const getSiteSeo = ({data}: {data: {fields: any[]}}) => {
  const description = get(data, 'description') || get(data, 'keywords');
  const title = get(data, 'title');
  return {description, title};
};

export const logger = (log: any) => {
  console.log('logger :>> ', JSON.stringify(log, null, 2));
};

function parseJSON(str: string | undefined) {
  if (str) {
    try {
      return JSON.parse(str);
    } catch (error) {
      return {};
    }
  }
  return {};
}

export function parseJSONWithData(str: string | undefined, key = 'data') {
  return _.get(parseJSON(str), key);
}

export const getBlocks = (page: any) => {
  const blocks: any[] = [];
  const sections = get(page, 'sections', []);
  for (const index in sections) {
    blocks.push(...get(sections, `[${index}].blocks`));
  }
  return blocks;
};

export const isMarkdown = (content: string) => {
  if (!content) {
    return false;
  }
  return !content?.includes('children') || !content?.includes('{"type');
};

const processFeatures = (page: any) => {
  const sections = get(page, 'sections', []);
  for (const index in sections) {
    let removedFeatures = _.remove(
      sections[index].blocks,
      function (block: any) {
        return block.style === 'features';
      },
    );

    let newObj = {
      id: index,
      style: 'features',
      features: removedFeatures,
    };
    if (newObj.features.length > 0) {
      sections[index].blocks.push(newObj);
    }
  }
};
